import React, { useState, useEffect, useRef } from 'react';
import useMobileBreakpoint from '@src/hooks/useMobileBreakpoint';
interface ComponentProps {
  block_id?: string;
  block_settings?: any;
  [key: string]: any;
}
interface PanelData {
  type: 'size' | 'color' | 'colour' | 'combined';
  isActive: boolean;
  blockId: string;
  options?: any[]; // Legacy support
  colourOptions?: any[];
  sizeOptions?: any[];
  selectedOption?: any; // Legacy support
  selectedColourOption?: any;
  selectedSizeOption?: any;
  displayStyle?: string;
  showColorNames?: boolean;
  swatchGap?: number;
  getColorValue?: (color: string) => string;
  onSelect?: (value: any) => void; // Legacy support
  onColorSelect?: (value: any) => void;
  onSizeSelect?: (value: any) => void;
}
export const StickySelectionPanel = (props: ComponentProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [panelData, setPanelData] = useState<PanelData | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobileBreakpoint(props.settings.globals.responsive_breakpoints?.mobile_max);
  // Extract settings from block_settings with defaults
  const settings = props.settings.block_settings || {};
  const {
    label_style,
    label_style_mobile,
    swatch_size = 20,
    swatch_size_mobile = 10,
    swatch_gap = 24,
    swatch_gap_mobile = 12,
    animation_duration = 300,
    panel_border_color = '#e5e7eb'
  } = settings;
  const block_id = props.block_id?.replace(/_/g, '').toLowerCase() || 'selection-panel';
  // Listen for panel toggle events
  useEffect(() => {
    const handleTogglePanel = (event: CustomEvent) => {
      const data = event.detail as PanelData;
      if (data.isActive) {
        setPanelData(data);
        setIsVisible(true);
      } else {
        setIsVisible(false);
        setTimeout(() => setPanelData(null), animation_duration);
      }
    };
    window.addEventListener('stickybar:togglePanel', handleTogglePanel as EventListener);
    return () => window.removeEventListener('stickybar:togglePanel', handleTogglePanel as EventListener);
  }, [animation_duration]);

  // Handle panel close
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => setPanelData(null), animation_duration);
    // Notify other components that panel is closed
    window.dispatchEvent(new CustomEvent('stickybar:panelClosed'));
  };
  const handleSizeSelect = (option: any) => {
    if (option.productHandle) {
      window.location.href = `/products/${option.productHandle}`;
      return;
    }
    // Only close on mobile or if not combined mode
    if (isMobile || panelData.type !== 'combined') {
      handleClose();
    }
  };
  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        // Don't close if clicking on a selector button
        if (!target.closest('[class*="sticky-size-selector"], [class*="sticky-color-selector"]')) {
          handleClose();
        }
      }
    };
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible]);
  const styles = {
    panel: {
      // borderBottom: `1px solid ${panel_border_color}`,
      // zIndex: 10,
      // display: 'flex',
      // alignItems: 'center',
      // padding: '16px 0px',
      // marginBottom: '16px'
    },
    panelMobile: {
      // borderBottom: `1px solid ${panel_border_color}`,
      // zIndex: 10,
      // display: 'flex',
      // alignItems: 'center',
      paddingBottom: '8px'
      // marginBottom: '16px'
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      margin: '0 auto'
    },
    leftSection: {
      display: 'flex',
      alignItems: 'center',
      width: '100%'
    },
    optionContainer: {
      borderBottom: `1px solid ${panel_border_color}`,
      paddingBottom: '16px',
      marginBottom: '16px'
    },
    optionsContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 0',
      gap: `${swatch_gap}px`,
      flexWrap: 'wrap' as const
    },
    colourOptionsContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 0',
      gap: `${swatch_gap}px`,
      flexWrap: 'wrap' as const
    },
    colourOptionsContainerMobile: {
      display: 'flex',
      alignItems: 'center',
      gap: `${swatch_gap_mobile}px`,
      flexWrap: 'wrap' as const
    },
    sizeOptionsContainerMobile: {
      display: 'flex',
      alignItems: 'center',
      gap: `8px`,
      flexWrap: 'wrap' as const
    },
    sizeOption: {
      padding: '12px',
      border: '1px solid transparent',
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      fontSize: '14px',
      letterSpacing: '0.5px',
      lineHeight: '140%',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      minWidth: '40px',
      textAlign: 'center' as const
    },
    sizeOptionMobile: {
      padding: '8px 12px',
      border: '1px solid transparent',
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      fontSize: '10px',
      letterSpacing: '0.5px',
      lineHeight: '120%',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      minWidth: '40px',
      textAlign: 'center' as const
    },
    sizeOptionSelected: {
      borderColor: '#C2C2C2',
      color: '#000000'
    },
    colorSwatch: {
      width: `${swatch_size}px`,
      height: `${swatch_size}px`,
      borderRadius: `2px`,
      border: '1px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative' as const
    },
    colorSwatchMobile: {
      width: `${swatch_size_mobile}px`,
      height: `${swatch_size_mobile}px`,
      borderRadius: `2px`,
      border: '1px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative' as const
    },
    colorSwatchSelected: {
      borderColor: 'black',
      cursor: 'default'
    },
    colorText: {
      fontSize: '12px',
      color: '#374151',
      textAlign: 'center' as const,
      marginTop: '4px',
      maxWidth: `${panelData?.swatchSize || 32}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const
    }
  };
  if (!panelData) return null;
  const renderSizeOptions = (titleOverride?: string) => {
    const sizeOptions = panelData.sizeOptions || panelData.options || [];

    return (
      <div style={{ display: 'flex', alignItems: isMobile ? 'start' : 'center', gap: isMobile ? '16px' : '24px' }}>
        <h4 className={`${isMobile ? label_style_mobile : label_style} ${isMobile ? '' : 'w-[75px]'}`}>
          {titleOverride || panelData.panelTitle || 'Size'}
        </h4>
        <div
          style={{
            ...(isMobile ? styles.sizeOptionsContainerMobile : styles.optionsContainer)
          }}
        >
          {sizeOptions.map((option: any, index: number) => {
            // Handle different option formats
            const optionValue = typeof option === 'string' ? option : option.title || option.name || option.id;
            const isSelected =
              panelData.selectedSizeOption === option ||
              panelData.selectedOption === option ||
              (typeof panelData.selectedSizeOption === 'object' && panelData.selectedSizeOption?.id === option.id);
            const additionalDetails = option.additionalDetails || '';
            return (
              <button
                key={option.id || index}
                style={{
                  ...(isMobile ? styles.sizeOptionMobile : styles.sizeOption),
                  ...(isSelected ? styles.sizeOptionSelected : {})
                }}
                onClick={() => handleSizeSelect(option)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    (e.target as HTMLElement).style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    (e.target as HTMLElement).style.backgroundColor = '#ffffff';
                  }
                }}
              >
                <span>{optionValue}</span>
                {additionalDetails && <span className='ml-1 lg:ml-3 opacity-50'>{additionalDetails}</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  const renderColourOptions = (titleOverride?: string) => {
    const colourOptions = panelData.colourOptions || panelData.options || [];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '16px' : '24px' }}>
        <h4 className={`${isMobile ? label_style_mobile : label_style} ${isMobile ? '' : 'w-[75px]'}`}>
          {titleOverride || panelData.panelTitle || 'Colour'}
        </h4>
        <div
          style={{
            ...(isMobile ? styles.colourOptionsContainerMobile : styles.colourOptionsContainer)
          }}
        >
          {colourOptions.map((colour: any, index: number) => (
            <a
              href={colour.url}
              key={colour.value || index}
              style={{ textAlign: 'center' }}
            >
              <div
                style={{
                  ...styles.colorSwatch,
                  ...(isMobile ? styles.colorSwatchMobile : {}),
                  ...(panelData.selectedColourOption === colour.name ? styles.colorSwatchSelected : {}),
                  ...(colour.swatch_type === 'image' && colour.value
                    ? {
                        backgroundImage: `url(${colour.value})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }
                    : {
                        backgroundColor: colour.value
                      })
                }}
                onMouseEnter={(e) => {
                  if (panelData.selectedColourOption !== colour.name) {
                    (e.target as HTMLElement).style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (panelData.selectedColourOption !== colour.name) {
                    (e.target as HTMLElement).style.transform = 'scale(1)';
                  }
                }}
              />
            </a>
          ))}
        </div>
      </div>
    );
  };
  return (
    <div
      ref={panelRef}
      className={`sticky-selection-panel-${block_id}`}
      style={isMobile ? styles.panelMobile : styles.panel}
      data-visible={isVisible}
    >
      <div style={styles.content}>
        <div style={styles.leftSection}>
          {(panelData.type === 'colour' || panelData.type === 'color') && renderColourOptions()}
          {panelData.type === 'size' && renderSizeOptions()}
          {panelData.type === 'combined' && (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              {panelData.colourOptions && panelData.colourOptions.length > 0 && (
                <div style={styles.optionContainer}>{renderColourOptions('Color')}</div>
              )}
              {panelData.sizeOptions &&
                panelData.sizeOptions.length > 0 &&
                panelData.sizeOptions.some((option) => option.id.includes(panelData.productId)) && (
                  <div style={styles.optionContainer}>{renderSizeOptions('Size')}</div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
